import { supabase } from '../supabase';
import { useSettingsStore } from '../stores/settingsStore';
import type { Deal, DealService, Client } from '../types';
import type { 
  EnhancedInvoiceQueueItem, 
  FICInvoiceFormData, 
  FICEntity, 
  FICItem,
  DocumentType,
  EInvoicePaymentMethod,
  PaymentTermsType,
  FICDocument
} from '../types/fattureincloud';
import { format, addDays } from 'date-fns';

/**
 * Risolve la data di EMISSIONE di una rata (speculare a
 * flusia-core/src/domains/workflows/lib/invoiceQueueGeneration.ts — tenere allineati).
 *
 * `fixedDate` è affidabile solo se scelta esplicitamente dall'utente
 * (daysAfterStart/days assenti o 0, nessun fixedDayOfMonth). I template dei
 * metodi di pagamento vengono materializzati alla CREAZIONE dell'offerta
 * (EnhancedDealServiceSelector.computeFixedDateFromTemplate) mantenendo
 * l'offset relativo accanto alla data: se il contratto viene firmato settimane
 * dopo, quella fixedDate è stantia e va ricalcolata da referenceDate (closed_at).
 */
function resolveInstallmentDueDate(inst: any, referenceDate: Date): Date {
  const relOffset = Number(inst?.daysAfterStart ?? inst?.days ?? 0) || 0;
  const templateDerived = relOffset > 0 || !!inst?.fixedDayOfMonth;
  if (inst?.fixedDate && !templateDerived) {
    return new Date(inst.fixedDate + 'T00:00:00');
  }
  if (inst?.fixedDayOfMonth) {
    const d = new Date(referenceDate);
    const targetDay = inst.fixedDayOfMonth as number;
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(targetDay, lastDay));
    if (inst.nextMonth) {
      d.setMonth(d.getMonth() + 1);
      const lastDayNew = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
      d.setDate(Math.min(targetDay, lastDayNew));
    }
    return d;
  }
  return addDays(referenceDate, relOffset);
}

/**
 * Enhanced Invoice Queue Service with full FattureInCloud support
 * Handles comprehensive invoice data including all FIC fields and options
 */
export const enhancedInvoiceQueueService = {
  /**
   * Generate enhanced queue items for a deal with comprehensive FIC data
   */
  generateEnhancedQueueItemsForDeal: async (dealId: string): Promise<number> => {
    try {
      // Fetch deal with all related data
      const { data: deal, error: dealError } = await supabase
        .from('deals')
        .select(`
          id,
          client_id,
          status,
          closed_at,
          created_at,
          title,
          description,
          amount,
          single_invoice_mode,
          single_invoice_date,
          client:clients (
            id,
            business_name,
            email,
            phone,
            vat_number,
            tax_code,
            street_address,
            city,
            postal_code,
            province,
            country,
            pec,
            sdi_code,
            entity_type
          ),
          payment_method:payment_methods (
            id,
            name,
            payment_schedule
          ),
          deal_services (
            id,
            service_id,
            custom_service_name,
            amount,
            discounted_amount,
            payment_method_id,
            payment_schedule,
            line_type,
            product_id,
            quantity,
            unit_of_measure,
            unit_price,
            vat_rate,
            invoice_description,
            service:services (
              id,
              title,
              description,
              auto_invoice,
              service_type,
              vat_rate
            ),
            payment_method:payment_methods (
              id,
              name,
              payment_schedule
            ),
            product:products (
              id,
              name,
              sku,
              vat_rate,
              commercial_name
            )
          )
        `)
        .eq('id', dealId)
        .single();

      if (dealError) throw dealError;
      if (!deal) throw new Error('Deal not found');

      // Fattura col nome commerciale degli articoli se l'org l'ha scelto
      // (settings.warehouse_settings.commercial_name_on_documents, richiesta Say
      // Farm). GEMELLO di flusia-core workflows/lib/invoiceQueueGeneration.ts.
      if ((useSettingsStore.getState().settings as any)?.warehouse_settings?.commercial_name_on_documents === true) {
        (deal as any).deal_services = withCommercialProductNames((deal as any).deal_services ?? []);
      }

      // Only generate for accepted deals
      if (deal.status !== 'accepted') {
        return 0;
      }

      // Get user and organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with organization');

      // Check for existing queue items
      const { count } = await supabase
        .from('enhanced_invoice_queue')
        .select('id', { count: 'exact', head: true })
        .eq('deal_id', dealId);

      if (count && count > 0) {
        return 0;
      }

      // Get reference date with validation
      let referenceDate = deal.closed_at ? new Date(deal.closed_at) : new Date(deal.created_at);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Validate reference date - if it's too far in the past (>90 days), use today
      const daysSinceReference = Math.floor((today.getTime() - referenceDate.getTime()) / (1000 * 60 * 60 * 24));
      if (daysSinceReference > 90) {
        console.warn(`[QUEUE] Deal ${dealId} has reference date ${daysSinceReference} days in the past, using today instead`);
        referenceDate = today;
      } else if (daysSinceReference < 0) {
        console.warn(`[QUEUE] Deal ${dealId} has reference date in the future, using today instead`);
        referenceDate = today;
      }

      // Create enhanced queue items
      const queueItems: Omit<EnhancedInvoiceQueueItem, 'id' | 'created_at' | 'updated_at'>[] = [];

      // Process each service
      for (const service of (deal.deal_services || [])) {
        // Righe prodotto (line_type='product'): vendita one-shot → rata unica
        // al 100% con emissione alla firma. GEMELLO del ramo equivalente in
        // flusia-core invoiceQueueGeneration.ts — tenere allineati.
        if (service.line_type === 'product') {
          const amount = Number(service.discounted_amount ?? service.amount ?? 0);
          if (amount <= 0) continue;
          let dueDate = new Date(referenceDate);
          if (dueDate < today) dueDate = new Date(today);
          queueItems.push({
            deal_id: dealId,
            deal_service_id: service.id,
            installment_index: 0,
            installment_description: 'Saldo unico',
            due_date: format(dueDate, 'yyyy-MM-dd'),
            payment_due_date: format(dueDate, 'yyyy-MM-dd'),
            amount,
            status: 'pending',
            fic_data: buildProductFicData({ deal, service, dueDate, amount }),
            created_by: userData.user.id,
            organization_id: userProfile.organization_id
          });
          continue;
        }

        // Servizi del catalogo: richiedono auto_invoice=true. Servizi custom (testo
        // libero, service_id null): fatturabili se hanno un payment_schedule valido
        // (verificato sotto). Decisione 2026-06-16.
        const isCustomService = !service.service_id;
        if (!isCustomService && !service.service?.auto_invoice) continue;

        // Determine payment schedule
        // Priorità: 1) Rate personalizzate del servizio, 2) Template del metodo di pagamento
        const paymentSchedule = service.payment_schedule?.installments?.length > 0
          ? service.payment_schedule
          : service.payment_method?.payment_schedule;
        if (!paymentSchedule?.installments) continue;

        // Validate total installments amount matches service amount
        const serviceAmount = service.discounted_amount || service.amount || 0;
        const totalInstallmentsAmount = paymentSchedule.installments.reduce(
          (sum: number, inst: any) => sum + (parseFloat(inst.amount || inst.value) || 0),
          0
        );

        if (Math.abs(serviceAmount - totalInstallmentsAmount) > 0.01) {
          console.warn(
            `[QUEUE] Service ${service.id} amount mismatch: service=${serviceAmount}, installments_total=${totalInstallmentsAmount}, difference=${serviceAmount - totalInstallmentsAmount}`
          );
        }

        // Process each installment
        paymentSchedule.installments.forEach((installment: any, index: number) => {
          // Finup R21: rate "data da destinarsi" — bypassano la generazione
          // automatica dei queue items. L'utente le sbloccherà manualmente
          // dalla pagina coda fatturazione definendone la data effettiva,
          // a quel punto verrà creata la queue row corrispondente.
          if (installment.awaiting_approval) {
            console.log(`[QUEUE] Service ${service.id} installment ${index} is awaiting_approval — skipped from auto-queue`);
            return;
          }
          // Calculate due date (le fixedDate materializzate da template vengono
          // ricalcolate da referenceDate: vedi resolveInstallmentDueDate)
          let dueDate: Date = resolveInstallmentDueDate(installment, referenceDate);

          // Calculate amount based on type - handle both pre-calculated and template installments
          let amount: number;
          if (installment.type === 'percentage') {
            // Always recalculate percentage-based amounts from service amount
            amount = serviceAmount * (parseFloat(installment.value) / 100);
          } else {
            // For fixed type, prefer amount field (can be number or string from DB)
            // First try to parse amount, then fall back to value
            const parsedAmount = typeof installment.amount === 'number'
              ? installment.amount
              : parseFloat(String(installment.amount));
            const parsedValue = typeof installment.value === 'number'
              ? installment.value
              : parseFloat(String(installment.value));

            // Use amount if it's a valid number (including 0), otherwise use value
            amount = !isNaN(parsedAmount) ? parsedAmount : (parsedValue || 0);
          }

          // Mai generare rate con emissione già passata: alla creazione coda una
          // data passata equivale a "fatturare subito" → oggi. Evita proforma e
          // fatture che nascono già scadute (→ solleciti FIC immediati al cliente).
          const dueDateObj = new Date(dueDate);
          dueDateObj.setHours(0, 0, 0, 0);
          if (dueDateObj < today) {
            const daysPast = Math.floor((today.getTime() - dueDateObj.getTime()) / (1000 * 60 * 60 * 24));
            console.warn(`[QUEUE] Installment ${index} for service ${service.id} had due_date ${daysPast} days in the past (${format(dueDate, 'yyyy-MM-dd')}) — clamped to today`);
            dueDate = new Date(today);
          }

          // Scadenza PAGAMENTO fattura (distinta dall'emissione = dueDate):
          // data fissa payment_due_date, oppure emissione + payment_terms_days.
          const paymentTermsDays = Number(installment.payment_terms_days ?? 0) || 0;
          let paymentDueDate: Date = installment.payment_due_date
            ? new Date(installment.payment_due_date + 'T00:00:00')
            : addDays(dueDate, paymentTermsDays);
          if (paymentDueDate < dueDate) paymentDueDate = new Date(dueDate);

          // Create comprehensive FIC data
          const ficData: FICInvoiceFormData = createComprehensiveFICData({
            deal,
            service,
            installment,
            installmentIndex: index,
            dueDate,
            amount,
            paymentTermsDays
          });

          queueItems.push({
            deal_id: dealId,
            deal_service_id: service.id,
            installment_index: index,
            installment_description: installment.description || `Rata ${index + 1}`,
            due_date: format(dueDate, 'yyyy-MM-dd'),
            payment_due_date: format(paymentDueDate, 'yyyy-MM-dd'),
            amount,
            status: 'pending',
            fic_data: ficData,
            created_by: userData.user.id,
            organization_id: userProfile.organization_id
          });
        });
      }

      // Handle single invoice mode - aggregate all items into one queue item
      if (deal.single_invoice_mode && queueItems.length > 0) {
        console.log(`[SINGLE INVOICE MODE] Aggregating ${queueItems.length} items for deal ${dealId}`);

        // Calculate total amount
        const totalAmount = queueItems.reduce((sum, item) => sum + item.amount, 0);

        // Use the single_invoice_date or fallback to today
        const invoiceDate = deal.single_invoice_date
          ? format(new Date(deal.single_invoice_date), 'yyyy-MM-dd')
          : format(new Date(), 'yyyy-MM-dd');

        // Build items_list for multiple line items (for FattureInCloud)
        const itemsList = queueItems.map(item => ({
          deal_service_id: item.deal_service_id,
          description: item.installment_description,
          amount: item.amount,
          qty: item.fic_data?.items?.[0]?.qty ?? 1,
          net_price: item.fic_data?.items?.[0]?.net_price ?? item.amount,
          measure: item.fic_data?.items?.[0]?.measure ?? ''
        }));

        // Build payments_list with due dates calculated from INVOICE DATE (not acceptance date)
        const invoiceDateObj = deal.single_invoice_date
          ? new Date(deal.single_invoice_date)
          : new Date();

        const paymentsList = queueItems.map((item) => {
          // Find the service and get the installment's daysAfterStart
          const service = deal.deal_services?.find((s: any) => s.id === item.deal_service_id);
          const paymentSchedule = service?.payment_schedule?.installments?.length > 0
            ? service.payment_schedule
            : service?.payment_method?.payment_schedule;
          const installment = paymentSchedule?.installments?.[item.installment_index];

          // Scadenza PAGAMENTO: data fissa payment_due_date, oppure emissione
          // risolta (resolveInstallmentDueDate) + payment_terms_days
          let paymentDueDate: Date;
          if (installment?.payment_due_date) {
            paymentDueDate = new Date(installment.payment_due_date + 'T00:00:00');
          } else {
            paymentDueDate = addDays(
              resolveInstallmentDueDate(installment ?? {}, invoiceDateObj),
              Number(installment?.payment_terms_days ?? 0) || 0
            );
          }
          // mai pagamenti che scadono prima della data fattura
          if (paymentDueDate < invoiceDateObj) paymentDueDate = new Date(invoiceDateObj);

          console.log(`[SINGLE INVOICE] Payment ${item.installment_index}: fixedDate=${installment?.fixedDate}, daysAfterStart=${installment?.daysAfterStart}, days=${installment?.days}`);

          // FIC: payments_list a importi LORDI (la somma deve dare il totale documento)
          const vatValue = Number(item.fic_data?.items?.[0]?.vat?.value ?? 22);
          return {
            amount: Math.round(item.amount * (1 + vatValue / 100) * 100) / 100,
            due_date: format(paymentDueDate, 'yyyy-MM-dd'),
            description: item.installment_description
          };
        });

        // Build aggregated FIC items for the invoice
        const aggregatedFicItems: FICItem[] = queueItems.map(item => ({
          name: item.fic_data?.items?.[0]?.name ?? item.installment_description ?? 'Servizio',
          description: item.fic_data?.items?.[0]?.description ?? item.installment_description ?? '',
          qty: item.fic_data?.items?.[0]?.qty ?? 1,
          measure: item.fic_data?.items?.[0]?.measure ?? 'nr',
          net_price: item.fic_data?.items?.[0]?.net_price ?? item.amount,
          vat: item.fic_data?.items?.[0]?.vat ?? { value: 22, description: '22%' },
          category: 'Servizi'
        }));

        // Create single aggregated queue item with modified fic_data
        const firstItem = queueItems[0];
        const singleQueueItem = {
          ...firstItem,
          amount: totalAmount,
          due_date: invoiceDate,
          installment_index: 0,
          installment_description: `Fattura unica - ${deal.title}`,
          fic_data: {
            ...firstItem.fic_data,
            subject: `Fattura unica - ${deal.title}`,
            date: invoiceDate,
            items: aggregatedFicItems,
            metadata: {
              single_invoice: true,
              items_list: itemsList,
              payments_list: paymentsList,
              invoice_date: invoiceDate
            }
          }
        };

        // Replace all items with the aggregated one
        queueItems.length = 0;
        queueItems.push(singleQueueItem);

        console.log(`[SINGLE INVOICE MODE] Created 1 aggregated queue item with ${itemsList.length} items`);
      }

      // Insert queue items
      if (queueItems.length > 0) {
        const { error: insertError } = await supabase
          .from('enhanced_invoice_queue')
          .insert(queueItems);

        if (insertError) throw insertError;
      }

      return queueItems.length;
    } catch (error) {
      console.error('Error generating enhanced queue items:', error);
      throw error;
    }
  },

  /**
   * Update queue item with modified FIC data
   */
  updateQueueItem: async (queueItemId: string, ficData: Partial<FICInvoiceFormData>): Promise<void> => {
    try {
      const updateData: Record<string, any> = {
        fic_data: ficData,
        updated_at: new Date().toISOString()
      };

      // Sincronizza due_date con fic_data.date (la data nel modale è la scadenza)
      if (ficData.date) {
        updateData.due_date = ficData.date;
      }

      const { error } = await supabase
        .from('enhanced_invoice_queue')
        .update(updateData)
        .eq('id', queueItemId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating queue item:', error);
      throw error;
    }
  },

  /**
   * Process queue item to FattureInCloud
   */
  processQueueItem: async (queueItemId: string): Promise<{ success: boolean; invoiceId?: number; error?: string }> => {
    try {
      // Get queue item
      const { data: queueItem, error: fetchError } = await supabase
        .from('enhanced_invoice_queue')
        .select('*')
        .eq('id', queueItemId)
        .single();

      if (fetchError) throw fetchError;
      if (!queueItem) throw new Error('Queue item not found');

      // Update status to processing
      await supabase
        .from('enhanced_invoice_queue')
        .update({ status: 'processing' })
        .eq('id', queueItemId);

      // Send to FattureInCloud
      const result = await sendInvoiceToFattureInCloud(queueItem.fic_data, queueItem);

      if (result.success) {
        // Update status to completed
        await supabase
          .from('enhanced_invoice_queue')
          .update({ 
            status: 'completed',
            updated_at: new Date().toISOString()
          })
          .eq('id', queueItemId);

        return { success: true, invoiceId: result.invoiceId };
      } else {
        // Update status to failed
        await supabase
          .from('enhanced_invoice_queue')
          .update({ 
            status: 'failed',
            error_message: result.error,
            retry_count: (queueItem.retry_count || 0) + 1,
            last_retry_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', queueItemId);

        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Error processing queue item:', error);
      
      // Update status to failed
      await supabase
        .from('enhanced_invoice_queue')
        .update({ 
          status: 'failed',
          error_message: error.message,
          updated_at: new Date().toISOString()
        })
        .eq('id', queueItemId);

      return { success: false, error: error.message };
    }
  },

  /**
   * Get queue items for a deal
   */
  getQueueItemsForDeal: async (dealId: string): Promise<EnhancedInvoiceQueueItem[]> => {
    try {
      const { data, error } = await supabase
        .from('enhanced_invoice_queue')
        .select('*')
        .eq('deal_id', dealId)
        .order('due_date', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting queue items:', error);
      throw error;
    }
  },

  /**
   * Delete queue item
   */
  deleteQueueItem: async (queueItemId: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('enhanced_invoice_queue')
        .delete()
        .eq('id', queueItemId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting queue item:', error);
      throw error;
    }
  },

  /**
   * Add a variable to the invoice queue with full FIC data
   */
  addVariableToQueue: async (params: {
    dealId: string;
    dealServiceId: string;
    variable: {
      id: string;
      name: string;
      description?: string;
      type: 'fixed' | 'percentage';
      amount: number;
      is_cost?: boolean;
      is_vat_exempt?: boolean;
      is_stamp_duty?: boolean;
    };
    calculatedAmount: number;
  }): Promise<string> => {
    try {
      const { dealId, dealServiceId, variable, calculatedAmount } = params;

      // Fetch deal with client data
      const { data: deal, error: dealError } = await supabase
        .from('deals')
        .select(`
          id,
          client_id,
          title,
          description,
          client:clients (
            id,
            business_name,
            email,
            phone,
            vat_number,
            tax_code,
            street_address,
            city,
            postal_code,
            province,
            country,
            pec,
            sdi_code,
            entity_type
          )
        `)
        .eq('id', dealId)
        .single();

      if (dealError) throw dealError;
      if (!deal) throw new Error('Deal not found');

      // Fetch service data
      const { data: service, error: serviceError } = await supabase
        .from('deal_services')
        .select(`
          id,
          service_id,
          amount,
          line_type,
          product_id,
          quantity,
          unit_of_measure,
          unit_price,
          vat_rate,
          service:services (
            id,
            title
          ),
          payment_method:payment_methods (
            id,
            name
          ),
          product:products (
            id,
            name,
            sku,
            vat_rate
          )
        `)
        .eq('id', dealServiceId)
        .single();

      if (serviceError) throw serviceError;

      // Get user and organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with organization');

      // Create FIC data similar to createComprehensiveFICData
      const client = deal.client as any;
      const entity: FICEntity = {
        name: client?.business_name || 'Cliente',
        type: client?.entity_type === 'company' ? 'company' : 'person',
        vat_number: client?.vat_number,
        tax_code: client?.tax_code,
        address_street: client?.street_address,
        address_city: client?.city,
        address_postal_code: client?.postal_code,
        address_province: client?.province,
        country: client?.country || 'Italy',
        email: client?.email,
        certified_email: client?.pec,
        phone: client?.phone
      };

      const serviceTitle = (service?.service as any)?.title || 'Servizio';
      // Righe prodotto (line_type='product'): qty/net_price/measure/vat dalle
      // colonne prodotto invece del default a riga singola. GEMELLO parità core.
      const isProductLine = (service as any)?.line_type === 'product';
      const variableQty = isProductLine ? (Number((service as any)?.quantity ?? 1) || 1) : 1;
      const variableNetPrice = isProductLine
        ? ((service as any)?.unit_price !== null && (service as any)?.unit_price !== undefined
          ? Number((service as any).unit_price)
          : Math.round((calculatedAmount / variableQty) * 100) / 100)
        : calculatedAmount;
      const variableMeasure = isProductLine ? ((service as any)?.unit_of_measure || 'nr') : 'nr';
      // Precedenza IVA: deal_services.vat_rate (riga) > products.vat_rate > 22
      const lineVatRate = (service as any)?.vat_rate !== null && (service as any)?.vat_rate !== undefined
        ? Number((service as any).vat_rate)
        : isProductLine && (service as any)?.product?.vat_rate !== null && (service as any)?.product?.vat_rate !== undefined
          ? Number((service as any).product.vat_rate)
          : 22;
      const items: FICItem[] = [{
        name: variable.name, // Usa il nome della variabile (es. "Marca da bollo")
        description: variable.description || `${variable.name} - ${serviceTitle}`,
        qty: variableQty,
        measure: variableMeasure,
        net_price: variableNetPrice,
        vat: variable.is_vat_exempt
          ? { value: 0, description: 'Esente IVA' }
          : { value: lineVatRate, description: `${lineVatRate}%` },
        category: variable.is_stamp_duty ? 'Bollo' : 'Servizi'
      }];

      const paymentMethod = service?.payment_method as any;
      const eiPaymentMethod: EInvoicePaymentMethod = mapPaymentMethodToEICode(paymentMethod?.name);

      const ficData: FICInvoiceFormData = {
        type: 'invoice',
        entity,
        date: format(new Date(), 'yyyy-MM-dd'),
        subject: `${deal.title} - ${variable.name}`,
        currency: 'EUR',
        language: 'it',
        items,
        payment_method: paymentMethod ? {
          id: paymentMethod.id,
          name: paymentMethod.name,
          ei_payment_method: eiPaymentMethod
        } : undefined,
        payment_terms: {
          days: 0,
          type: 'standard' as PaymentTermsType
        },
        use_split_payment: false,
        use_gross_prices: false,
        e_invoice: client?.entity_type === 'company' && (client?.vat_number || client?.tax_code),
        ei_type: client?.entity_type === 'company' ? 'B2B' : 'B2C',
        ei_data: {
          payment_method: eiPaymentMethod
        },
        notes: variable.description || deal.description || ''
      };

      // Insert into enhanced_invoice_queue
      // Note: metadata for variable info is stored in fic_data.notes or installment_description
      const { data: queueItem, error: insertError } = await supabase
        .from('enhanced_invoice_queue')
        .insert({
          deal_id: dealId,
          deal_service_id: dealServiceId,
          installment_index: 0,
          installment_description: `${variable.name} - ${serviceTitle}`,
          due_date: format(new Date(), 'yyyy-MM-dd'),
          amount: calculatedAmount,
          status: 'pending',
          fic_data: ficData,
          created_by: userData.user.id,
          organization_id: userProfile.organization_id
        })
        .select('id')
        .single();

      if (insertError) throw insertError;
      if (!queueItem) throw new Error('Failed to create queue item');

      return queueItem.id;
    } catch (error) {
      console.error('Error adding variable to queue:', error);
      throw error;
    }
  },

  /**
   * Finup R21: sblocca una rata in stato "data da destinarsi"
   * (awaiting_approval=true) impostandone la data effettiva e
   * inserendola subito nella coda di fatturazione.
   *
   * Operazioni:
   *  1) Carica il deal_service + deal + client per costruire i dati FIC
   *  2) Rimuove il flag awaiting_approval sull'installment indicato e setta
   *     fixedDate = newDueDate (ISO YYYY-MM-DD)
   *  3) Inserisce 1 nuova row in enhanced_invoice_queue con stato pending
   *  4) Persiste il payment_schedule aggiornato sul deal_service
   */
  unblockInstallment: async (
    dealServiceId: string,
    installmentIndex: number,
    newDueDate: string, // ISO YYYY-MM-DD
  ): Promise<string> => {
    // Audit R21: validazione formato data + bounds index PRIMA del DB.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newDueDate)) {
      throw new Error(`Invalid newDueDate format (expected YYYY-MM-DD): ${newDueDate}`);
    }
    if (!Number.isInteger(installmentIndex) || installmentIndex < 0) {
      throw new Error(`Invalid installmentIndex: ${installmentIndex}`);
    }

    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new Error('User not authenticated');
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('organization_id')
      .eq('id', userData.user.id)
      .single();
    if (!profile?.organization_id) throw new Error('Missing organization');

    // 1) Fetch deal_service (con join service + payment_method + deal + client).
    // Audit R21: organization_id check esplicito (defense-in-depth oltre a RLS).
    //
    // Il controllo passa dall'offerta, NON da `deal_services.organization_id`:
    // quella colonna è nullable e i percorsi di inserimento dell'app non la
    // valorizzano, quindi risulta NULL su gran parte delle righe. Filtrandola
    // direttamente, questa fetch non trovava la riga e "sblocca rata" falliva
    // con "not found or not accessible" su una rata che la lista mostrava
    // regolarmente (PendingApprovalInstallments carica passando da `deals`).
    // `deals.organization_id` è invece sempre valorizzata, quindi la catena di
    // proprietà via `deals!inner` è equivalente e completa.
    const { data: ds, error: dsErr } = await supabase
      .from('deal_services')
      .select(`
        *,
        service:services(id, title, description, auto_invoice, service_type, vat_rate),
        payment_method:payment_methods(id, name, payment_schedule),
        deal:deals!inner(
          id, title, description, amount, single_invoice_mode, single_invoice_date,
          client:clients(id, business_name, email, phone, vat_number, tax_code,
            street_address, city, postal_code, province, country, pec, sdi_code, entity_type)
        )
      `)
      .eq('id', dealServiceId)
      .eq('deal.organization_id', profile.organization_id)
      .single();
    if (dsErr || !ds) throw new Error(`deal_service ${dealServiceId} not found or not accessible`);

    const schedule = ds.payment_schedule as any;
    if (!Array.isArray(schedule?.installments) || installmentIndex >= schedule.installments.length) {
      throw new Error(`Installment ${installmentIndex} not found on deal_service ${dealServiceId}`);
    }
    const installment = { ...schedule.installments[installmentIndex] };

    // 2) Aggiorna l'installment in memoria
    installment.awaiting_approval = false;
    installment.fixedDate = newDueDate;
    installment.daysAfterStart = 0;
    installment.fixedDayOfMonth = undefined;
    const updatedInstallments = schedule.installments.map((it: any, i: number) =>
      i === installmentIndex ? installment : it,
    );
    const updatedSchedule = { ...schedule, installments: updatedInstallments };

    // 3) Calcola amount per la rata (percentage → service amount × value/100)
    const serviceAmount = (ds.discounted_amount ?? ds.amount) ?? 0;
    let amount: number;
    if (installment.type === 'percentage') {
      amount = serviceAmount * (parseFloat(installment.value) / 100);
    } else {
      amount = parseFloat(installment.amount ?? installment.value) || 0;
    }

    // 4) Costruisci fic_data via createComprehensiveFICData (richiede il deal con services)
    const dealForFic = {
      ...ds.deal,
      client: ds.deal.client,
      description: ds.deal.description,
    };
    const serviceForFic = {
      ...ds,
      service: ds.service,
      payment_method: ds.payment_method,
    };
    const ficData: FICInvoiceFormData = createComprehensiveFICData({
      deal: dealForFic,
      service: serviceForFic,
      installment,
      installmentIndex,
      dueDate: new Date(newDueDate + 'T00:00:00'),
      amount,
    });

    // 5) INSERT queue item + UPDATE deal_service.payment_schedule in parallelo
    const [{ data: queueItem, error: insertErr }, { error: updateErr }] = await Promise.all([
      supabase
        .from('enhanced_invoice_queue')
        .insert({
          deal_id: ds.deal.id,
          deal_service_id: dealServiceId,
          installment_index: installmentIndex,
          installment_description: installment.description || `Rata ${installmentIndex + 1}`,
          due_date: newDueDate,
          amount,
          status: 'pending',
          fic_data: ficData,
          created_by: userData.user.id,
          organization_id: profile.organization_id,
        })
        .select('id')
        .single(),
      // Audit R21: la proprietà della riga è già stata verificata dalla fetch al
      // punto 1, che filtra per `deal.organization_id`. Qui NON si può ripetere
      // il filtro su `deal_services.organization_id` (nullable, spesso NULL):
      // l'UPDATE non aggancerebbe nessuna riga e — non essendo un errore per
      // PostgREST — il piano rate non verrebbe salvato in silenzio.
      supabase
        .from('deal_services')
        .update({ payment_schedule: updatedSchedule })
        .eq('id', dealServiceId),
    ]);
    if (insertErr) throw insertErr;
    if (updateErr) throw updateErr;
    if (!queueItem) throw new Error('Failed to insert queue item');
    return queueItem.id;
  },
};

/**
 * Costruisce la FICEntity da un client CRM. Estratta per essere riusata sia da
 * createComprehensiveFICData (righe servizio) sia da buildProductFicData
 * (righe prodotto) — GEMELLO di buildEntityFromClient in
 * flusia-core/src/domains/workflows/lib/invoiceQueueGeneration.ts.
 */
function buildEntityFromClient(client: any): FICEntity {
  return {
    name: client?.business_name || 'Cliente',
    type: client?.entity_type === 'company' ? 'company' : 'person',
    vat_number: client?.vat_number,
    tax_code: client?.tax_code,
    address_street: client?.street_address,
    address_city: client?.city,
    address_postal_code: client?.postal_code,
    address_province: client?.province,
    country: client?.country || 'Italy',
    email: client?.email,
    certified_email: client?.pec,
    phone: client?.phone
  };
}

/**
 * fic_data per una riga prodotto (deal_services.line_type='product').
 * GEMELLO di flusia-core/src/domains/workflows/lib/invoiceQueueGeneration.ts
 * buildProductFicData — tenere allineati (parità core/frontend).
 */
/**
 * Righe dell'offerta con il nome commerciale dell'articolo al posto di quello
 * magazzino, dove compilato. Gemello di flusia-core withCommercialProductNames.
 */
function withCommercialProductNames<T extends { product?: { name?: string | null; commercial_name?: string | null } | null }>(rows: T[]): T[] {
  return rows.map((row) => {
    const commercialName = row.product?.commercial_name?.trim();
    return commercialName ? { ...row, product: { ...row.product, name: commercialName } } : row;
  });
}

function buildProductFicData({
  deal,
  service,
  amount
}: {
  deal: any;
  service: any; // riga deal_services con join product:products
  dueDate: Date;
  amount: number;
}): FICInvoiceFormData {
  const client = deal.client ?? {};
  const qty = Number(service.quantity ?? 1) || 1;
  const unitPrice = service.unit_price !== null && service.unit_price !== undefined
    ? Number(service.unit_price)
    : Math.round((amount / qty) * 100) / 100;
  // Precedenza IVA: deal_services.vat_rate (riga) > products.vat_rate > 22
  const vatRate = service.vat_rate !== null && service.vat_rate !== undefined
    ? Number(service.vat_rate)
    : (service.product?.vat_rate === null || service.product?.vat_rate === undefined
      ? 22
      : Number(service.product.vat_rate));
  const productName = service.product?.name ?? service.custom_service_name ?? 'Articolo';
  const description = service.invoice_description?.trim()
    || (service.product?.sku ? `SKU: ${service.product.sku}` : '');

  // Le righe prodotto non hanno un metodo di pagamento proprio: lo prendono
  // dall'offerta. Senza questo la fattura usciva senza metodo e con zero giorni
  // di termine, cioe' a pagamento immediato — un "bonifico 30 giorni" era
  // inesprimibile su un'offerta di soli prodotti.
  const paymentMethod = deal.payment_method;
  const eiPaymentMethod = mapPaymentMethodToEICode(paymentMethod?.name);
  const paymentTermsDays = Number(
    paymentMethod?.payment_schedule?.installments?.[0]?.payment_terms_days ?? 0
  ) || 0;

  return {
    type: 'invoice',
    entity: buildEntityFromClient(client),
    date: format(new Date(), 'yyyy-MM-dd'),
    subject: `${deal.title} - ${productName}`,
    currency: 'EUR',
    language: 'it',
    items: [{
      name: productName,
      description,
      qty,
      measure: service.unit_of_measure || 'nr',
      net_price: unitPrice,
      vat: { value: vatRate, description: `${vatRate}%` },
      category: 'Prodotti'
    }],
    payment_method: paymentMethod ? {
      id: paymentMethod.id,
      name: paymentMethod.name,
      ei_payment_method: eiPaymentMethod
    } : undefined,
    payment_terms: { days: Math.max(0, paymentTermsDays), type: 'standard' as PaymentTermsType },
    use_split_payment: false,
    use_gross_prices: false,
    e_invoice: client.entity_type === 'company' && (client.vat_number || client.tax_code),
    ei_type: client.entity_type === 'company' ? 'B2B' : 'B2C',
    ei_data: { payment_method: eiPaymentMethod },
    notes: deal.description || ''
  };
}

/**
 * Create comprehensive FattureInCloud data from deal and service info
 */
function createComprehensiveFICData({
  deal,
  service,
  installment,
  installmentIndex,
  dueDate,
  amount,
  paymentTermsDays = 0
}: {
  deal: any;
  service: any;
  installment: any;
  installmentIndex: number;
  dueDate: Date;
  amount: number;
  paymentTermsDays?: number;
}): FICInvoiceFormData {
  const client = deal.client;

  // Create entity data
  const entity: FICEntity = buildEntityFromClient(client);

  // Create item data
  // Righe prodotto (line_type='product'): qty/net_price/measure/vat dalle
  // colonne prodotto invece del default a riga singola/servizio. In pratica
  // questa funzione viene chiamata solo per righe servizio (le righe prodotto
  // escono dal loop principale prima di arrivare qui — vedi ramo dedicato in
  // generateEnhancedQueueItemsForDeal), ma il branch resta per parità con il
  // core e a scopo difensivo (es. futuri call site come unblockInstallment).
  const isProductLine = service.line_type === 'product';
  const qty = isProductLine ? (Number(service.quantity ?? 1) || 1) : 1;
  // Precedenza IVA: deal_services.vat_rate (riga) > products/services.vat_rate > 22
  const vatRate = service.vat_rate !== null && service.vat_rate !== undefined
    ? Number(service.vat_rate)
    : isProductLine
      ? (service.product?.vat_rate === null || service.product?.vat_rate === undefined ? 22 : Number(service.product.vat_rate))
      : (service.service?.vat_rate === null || service.service?.vat_rate === undefined ? 22 : Number(service.service.vat_rate));
  const netPrice = isProductLine
    ? (service.unit_price !== null && service.unit_price !== undefined ? Number(service.unit_price) : Math.round((amount / qty) * 100) / 100)
    : amount;
  const measure = isProductLine ? (service.unit_of_measure || 'nr') : 'nr';
  const serviceTitle = service.service?.title ?? service.custom_service_name ?? 'Servizio';
  const items: FICItem[] = [{
    name: serviceTitle,
    description: generateInstallmentDescription(
      serviceTitle,
      installment.description,
      installmentIndex,
      amount,
      service.invoice_description
    ),
    qty,
    measure,
    net_price: netPrice,
    vat: {
      value: vatRate,
      description: `${vatRate}%`
    },
    category: 'Servizi'
  }];

  // Determine payment method and EI code
  const paymentMethod = service.payment_method;
  const eiPaymentMethod: EInvoicePaymentMethod = mapPaymentMethodToEICode(paymentMethod?.name);

  // Create comprehensive FIC data
  const ficData: FICInvoiceFormData = {
    type: 'invoice',
    entity,
    date: format(new Date(), 'yyyy-MM-dd'),
    subject: `${deal.title} - ${installment.description || `Rata ${installmentIndex + 1}`}`,
    currency: 'EUR',
    language: 'it',
    items,
    
    // Payment settings
    payment_method: paymentMethod ? {
      id: paymentMethod.id,
      name: paymentMethod.name,
      ei_payment_method: eiPaymentMethod
    } : undefined,
    
    // Giorni dall'EMISSIONE alla scadenza PAGAMENTO (non da oggi alla emissione).
    payment_terms: {
      days: Math.max(0, paymentTermsDays),
      type: 'standard' as PaymentTermsType
    },

    // Tax settings
    use_split_payment: false,
    use_gross_prices: false,
    
    // E-invoice settings
    e_invoice: client.entity_type === 'company' && (client.vat_number || client.tax_code),
    ei_type: client.entity_type === 'company' ? 'B2B' : 'B2C',
    ei_data: {
      payment_method: eiPaymentMethod
    },

    // Notes
    notes: deal.description || ''
  };

  return ficData;
}

/**
 * Generate intelligent installment description.
 * When `invoiceDescriptionOverride` is provided (per `deal_service.invoice_description`),
 * lo usa come base al posto del titolo del servizio. Se è presente anche una
 * `installmentDesc` (es. "Rata 1/3"), la appende per mantenere il riferimento
 * alla rata. Altrimenti fallback al comportamento storico
 * ({serviceTitle} - {installmentDesc | "Rata N (€amount)"}).
 */
function generateInstallmentDescription(
  serviceTitle: string,
  installmentDesc: string | undefined,
  index: number,
  amount: number,
  invoiceDescriptionOverride?: string | null
): string {
  const override = invoiceDescriptionOverride?.trim();
  if (override) {
    return installmentDesc ? `${override} - ${installmentDesc}` : override;
  }

  if (installmentDesc) {
    return `${serviceTitle} - ${installmentDesc}`;
  }

  return `${serviceTitle} - Rata ${index + 1} (€${amount.toFixed(2)})`;
}

/**
 * Map payment method name to e-invoice code
 */
function mapPaymentMethodToEICode(paymentMethodName: string | undefined): EInvoicePaymentMethod {
  if (!paymentMethodName) return 'MP05'; // Default to bank transfer

  const methodName = paymentMethodName.toLowerCase();
  
  if (methodName.includes('contant') || methodName.includes('cash')) return 'MP01';
  if (methodName.includes('assegno') || methodName.includes('check')) return 'MP02';
  if (methodName.includes('bonifico') || methodName.includes('transfer')) return 'MP05';
  if (methodName.includes('carta') || methodName.includes('card')) return 'MP08';
  if (methodName.includes('rid') || methodName.includes('sdd')) return 'MP09';
  if (methodName.includes('paypal')) return 'MP08';
  if (methodName.includes('stripe')) return 'MP08';
  
  return 'MP05'; // Default to bank transfer
}

/**
 * Send invoice to FattureInCloud via edge function
 */
async function sendInvoiceToFattureInCloud(
  ficData: FICInvoiceFormData,
  queueItem: any
): Promise<{ success: boolean; invoiceId?: number; error?: string; warning?: any }> {
  try {
    // Get user profile for organization_id
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new Error('User not authenticated');

    const { data: userProfile } = await supabase
      .from('user_profiles')
      .select('organization_id')
      .eq('id', userData.user.id)
      .single();

    if (!userProfile?.organization_id) throw new Error('User not associated with organization');

    // Determina l'indice della rata - fallback su descrizione per record vecchi
    let installmentIndex = queueItem.installment_index;
    if (installmentIndex === null || installmentIndex === undefined) {
      // Fallback: estrai dalla descrizione "1 Rata", "2 Rata", "Setup CRM - 2 Rata", etc.
      const match = queueItem.installment_description?.match(/(\d+)\s*[Rr]ata/);
      if (match) {
        installmentIndex = parseInt(match[1]) - 1; // "2 Rata" → 1
      } else {
        installmentIndex = 0;
      }
      console.log(`[FALLBACK] installment_index was null, extracted ${installmentIndex} from description "${queueItem.installment_description}"`);
    }

    // Data documento = data di EMISSIONE (oggi), NON la data congelata in
    // fic_data alla generazione coda: una rata processata settimane dopo
    // nascerebbe con data/scadenza nel passato (→ solleciti FIC immediati).
    // Eccezione: fattura unica, dove la data è una scelta esplicita dell'utente.
    const isSingleInvoice = ficData.metadata?.single_invoice === true;
    const invoiceIssueDate = isSingleInvoice
      ? (ficData.date || format(new Date(), 'yyyy-MM-dd'))
      : format(new Date(), 'yyyy-MM-dd');

    // Prepare invoice data
    const invoiceData = {
      date: invoiceIssueDate,
      type: ficData.type || 'invoice',
      amount: queueItem.amount,
      installment_description: queueItem.installment_description,
      installment_index: installmentIndex, // Per determinare se è la prima rata (per stamp duty Art. 15)
      notes: ficData.notes || ficData.subject,
      // Include all FIC data
      entity: ficData.entity,
      items: ficData.items,
      payment_terms: ficData.payment_terms,
      e_invoice: ficData.e_invoice,
      ei_type: ficData.ei_type,
      ei_data: ficData.ei_data,
      metadata: ficData.metadata,  // Include metadata with payments_list for single invoice mode
      // Rivalsa INPS e metodo di pagamento
      rivalsa: ficData.rivalsa,           // Rivalsa INPS (es: 4 per 4%)
      payment_method: ficData.payment_method,  // Metodo di pagamento per PDF
      // Scadenza effettiva dal queue item (FIX: non usare data odierna + payment_terms)
      payments_list: [
        {
          amount: queueItem.amount,
          due_date: queueItem.due_date,  // Data scadenza corretta dal queue item
          payment_terms: { days: 0, type: "standard" },
          status: "not_paid"
        }
      ]
    };

    console.log('Calling fattureincloud-invoice edge function:', invoiceData);

    // Call edge function
    const { data: result, error } = await supabase.functions.invoke('fattureincloud-invoice', {
      body: {
        action: 'create',
        organization_id: userProfile.organization_id,
        deal_id: queueItem.deal_id,
        deal_service_id: queueItem.deal_service_id,
        invoice_data: invoiceData
      }
    });

    if (error) throw error;
    if (!result.success) throw new Error(result.error || 'Failed to create invoice');

    return {
      success: true,
      invoiceId: result.fattureincloud_id,
      warning: result.warning
    };
  } catch (error: any) {
    console.error('Error sending invoice to FattureInCloud:', error);
    return {
      success: false,
      error: error.message
    };
  }
}